import { yupResolver } from '@hookform/resolvers/yup'
import InfoIconGrey from 'assets/icons/info-grey-icon.svg?component'
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/UI/form'
import InputMasked from 'components/UI/Inputs/InputMasked'
import LandingContainer from 'components/UI/Landing/container'
import SegmentedControl from 'components/UI/SegmentedControl'
import Typography from 'components/UI/Typography'
import { useEffect, useMemo, useState } from 'react'
import { type Control, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { useCalculateCreditMutation, useGetCreditProgramsQuery } from 'redux/api/creditApi'
import { RepaymentMethods } from 'share/enums/payment'
import { ApplicationProgramCode } from 'share/types'
import { CreditProgram } from 'share/types/api/credits'
import { LoanPurposes } from 'share/types/api/main'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import { pathnames } from 'utils/constants'
import LoanCalculator from 'utils/loan-calculator'
import { getRepaymentMethodName } from 'utils/translations'

import { CalcResultLabel, CalcResultText, Card, Text } from './base'
import {
  CalculatorSchemaValues,
  createCalculatorSchema,
  dummySchema,
  getProgramName,
  REPAYMENT_METHODS
} from './helpers'

const SuperappCalculator = () => {
  // Tools
  const { t } = useTranslation('landing')
  const navigate = useNavigate()
  const qs = new URLSearchParams(window.location.search)

  // State
  const [programCode, setProgramCode] = useState<ApplicationProgramCode>('OWN_PROGRAM')
  const [subProgramCode, setSubProgramCode] = useState<LoanPurposes | number>(() => {
    if (programCode === 'DAMU') return 'INVESTMENTS'
    return 0 // without pledge
  })
  const [repaymentMethod, setRepaymentMethod] = useState<RepaymentMethods>(RepaymentMethods.ANNUITY)

  // Queries
  const { data } = useGetCreditProgramsQuery()

  // Mutations
  const [calculateCredit, { isLoading: isCalculateCreditLoading }] = useCalculateCreditMutation()

  // Computed values
  const availableProgramCodes = useMemo(() => {
    if (!data) return []
    return Array.from(new Set(data.data.map(p => p.program_code)))
  }, [data])

  const filteredPrograms = useMemo(() => {
    if (!data) return []
    return data.data.filter(p => p.program_code === programCode)
  }, [data, programCode])

  const matchedProgram = useMemo(() => {
    if (programCode === 'OWN_PROGRAM') {
      return filteredPrograms.find(p => p.with_pledges === !!subProgramCode)
    }
    return filteredPrograms.find(p => p.loan_purpose_verbose === subProgramCode)
  }, [filteredPrograms, programCode, subProgramCode])

  // Form
  const schema = useMemo(() => {
    if (!matchedProgram) return dummySchema
    return createCalculatorSchema({
      minAmount: +matchedProgram.min_loan_amount,
      maxAmount: +matchedProgram.max_loan_amount,
      minPeriod: matchedProgram.min_period,
      maxPeriod: matchedProgram.max_period
    })
  }, [matchedProgram])

  const form = useForm<CalculatorSchemaValues>({
    resolver: yupResolver(schema),
    mode: 'all'
  })

  // Handlers
  const handleProgramCodeChange = (value: ApplicationProgramCode) => {
    setProgramCode(value)
    setSubProgramCode(value === 'DAMU' ? 'INVESTMENTS' : 0)
  }

  const handleSubProgramCodeChange = async (value: LoanPurposes | number) => {
    setSubProgramCode(value)
  }

  const handleVerifyCalculation = async (data: CalculatorSchemaValues) => {
    try {
      const res = await calculateCredit({
        repayment_method: repaymentMethod,
        amount: +data.amount,
        period: +data.period
      }).unwrap()
      navigate({
        pathname: pathnames['create-lead'],
        search: createSearchParams({
          'interest_rate': '' + res.program.client_interest_rate,
          'period': '' + res.period,
          'amount': '' + res.amount,
          'program': res.program.id,
          'gesv': '' + res.gesv,
          'monthly_payment': res.payment.toString(),
          'repayment_method': res.repayment_method,
          'data': qs.get('data'),
          'appType': qs.get('appType'),
        }).toString()
      })
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (matchedProgram) {
      form.reset({ amount: '', period: '' })
    }
  }, [form, matchedProgram])

  return (
    <Form {...form}>
      <LandingContainer twStyle={tw`px-4 pt-4 pb-8 bg-[#F7F7F7] dark:bg-[#000000]`}>
        <SegmentedControl
          value={programCode}
          options={availableProgramCodes.map(code => ({
            value: code,
            content: <Typography twStyle={tw`text-sm leading-5 font-medium`}>{getProgramName(code)}</Typography>
          }))}
          onChange={handleProgramCodeChange}
        />
        <Card twStyle={tw`mt-4`}>
          <Text>
            {t(programCode === 'OWN_PROGRAM' ? 'calculator-section.pledge-title' : 'calculator-section.goal')}
          </Text>
          <SegmentedControl
            className='mt-2'
            value={subProgramCode}
            twStyle={tw`p-0 !bg-transparent gap-x-2 overflow-x-auto mt-3`}
            segmentStyle={tw`flex-none px-4 py-2 border border-[#3A3A3C] text-[#636366] rounded-xl dark:border-[#F2F2F7] dark:text-[#D1D1D6]`}
            activeSegmentStyle={tw`bg-[#3A3A3C] text-[#FFFFFF] dark:bg-[#F2F2F7] dark:text-[#000000]`}
            options={filteredPrograms.map(p => ({
              value: programCode === 'OWN_PROGRAM' ? +p.with_pledges : p.loan_purpose_verbose,
              content: (
                <Typography twStyle={tw`text-sm whitespace-nowrap`}>
                  {programCode === 'OWN_PROGRAM'
                    ? t(p.with_pledges ? 'calculator-section.with-pledge' : 'calculator-section.without-pledge')
                    : t(
                        p.loan_purpose_verbose === 'REPLENISHMENT'
                          ? 'calculator-section.replenishment'
                          : 'loan-terms.terms-card.tabs.investments'
                      )}
                </Typography>
              )
            }))}
            onChange={handleSubProgramCodeChange}
          />
          <hr className={'my-4 dark:border-[#2B2B2E]'} />
          <Text>{t('calculator-section.loan-amount')}</Text>
          <FormField
            key={matchedProgram?.id}
            control={form.control}
            name='amount'
            render={({ field: { value, ref, onChange }, fieldState: { invalid } }) => (
              <FormItem className='mt-3'>
                <FormControl>
                  <InputMasked
                    ref={ref}
                    value={value}
                    inputClassName={tw`rounded-2xl bg-[#EAECED] w-full text-center h-14 text-lg dark:text-[var(--color-swiper-body-text)] dark:bg-[var(--color-dark)]`}
                    placeholder={t('calculator-section.fromPrice', {
                      minAmount: numberWithSpaces(Number(matchedProgram?.min_loan_amount ?? 500_000))
                    })}
                    isError={invalid}
                    onAccept={onChange}
                  />
                </FormControl>
                {invalid ? (
                  <FormMessage tw='mt-3 text-[13px] leading-[18px] font-medium text-[#FF3347] text-center' />
                ) : (
                  <div tw='flex justify-between mt-3'>
                    <Text>
                      {t('calculator-section.fromPrice', {
                        minAmount: numberWithSpaces(Number(matchedProgram?.min_loan_amount ?? 0))
                      })}
                    </Text>
                    <Text>
                      {t('calculator-section.toPrice', {
                        maxAmount: numberWithSpaces(Number(matchedProgram?.max_loan_amount ?? 0))
                      })}
                    </Text>
                  </div>
                )}
              </FormItem>
            )}
          />
          <hr className={'my-4 dark:border-[#2B2B2E]'} />
          <Text>{t('calculator-section.loan-period')}</Text>
          <FormField
            control={form.control}
            name='period'
            render={({ field: { value, onChange, ref }, fieldState: { invalid } }) => (
              <FormItem className='mt-3'>
                <FormControl>
                  <InputMasked
                    ref={ref}
                    value={value}
                    inputClassName={tw`rounded-2xl bg-[#EAECED] w-full text-center h-14 text-lg dark:text-[var(--color-swiper-body-text)] dark:bg-[var(--color-dark)]`}
                    currency={t('calculator-section.month') as string}
                    placeholder={t('calculator-section.month-input-placeholder', {
                      fromMonth: Number(matchedProgram?.min_period ?? 6)
                    })}
                    isError={invalid}
                    onAccept={onChange}
                  />
                </FormControl>
                {invalid ? (
                  <FormMessage tw='text-[13px] leading-[18px] font-medium text-[#FF3347] text-center' />
                ) : (
                  <div tw='flex justify-between mt-3'>
                    <Text>
                      {t('calculator-section.fromMonth', { fromMonth: Number(matchedProgram?.min_period ?? 0) })}
                    </Text>
                    <Text>{t('calculator-section.toMonth', { toMonth: Number(matchedProgram?.max_period ?? 0) })}</Text>
                  </div>
                )}
              </FormItem>
            )}
          />
          <hr className={'my-4 dark:border-[#2B2B2E]'} />
          <Text>{t('loan.repayment-method', { ns: 'common' })}</Text>
          <SegmentedControl
            twStyle={tw`mt-3`}
            value={repaymentMethod}
            options={REPAYMENT_METHODS.map(method => ({
              value: method,
              content: (
                <Typography twStyle={tw`text-sm leading-5 font-medium`}>
                  {t(getRepaymentMethodName(method), { ns: 'common' })}
                </Typography>
              )
            }))}
            onChange={setRepaymentMethod}
          />
        </Card>
        <Card twStyle={tw`mt-4 p-3 flex items-center gap-x-2`}>
          <InfoIconGrey tw='flex-shrink-0' />
          <Text>{t('calculator-section.warning-text-1')}</Text>
        </Card>
        <CalculatorResults
          control={form.control}
          rate={matchedProgram?.client_interest_rate}
          repaymentMethod={repaymentMethod}
          matchedProgram={matchedProgram}
        />
        <Card twStyle={tw`mt-4 p-3 flex items-center gap-x-2`}>
          <InfoIconGrey tw='flex-shrink-0' />
          <Text>{t('calculator-section.warning-text-2')}</Text>
        </Card>
        <button
          disabled={isCalculateCreditLoading || !form.formState.isValid}
          tw='mt-6 w-full py-3.5 rounded-2xl text-white font-semibold bg-[#4EBC73] disabled:bg-[#B8E4C7] dark:disabled:bg-[#11583C]'
          onClick={form.handleSubmit(handleVerifyCalculation)}
        >
          {t('mobile-menu.btn-text')}
        </button>
      </LandingContainer>
    </Form>
  )
}

const CalculatorResults = ({
  control,
  rate,
  repaymentMethod,
  matchedProgram
}: {
  control: Control<CalculatorSchemaValues>
  rate?: number
  repaymentMethod: RepaymentMethods
  matchedProgram?: CreditProgram
}) => {
  // Tools
  const { t } = useTranslation('translation')

  // Form
  const amount = useWatch({ control, name: 'amount' })
  const period = useWatch({ control, name: 'period' })

  // Computed values
  const calcResults = useMemo(() => {
    if (
      !amount ||
      !period ||
      !rate ||
      !matchedProgram ||
      +period < matchedProgram.min_period ||
      +period > matchedProgram.max_period
    ) {
      return { gesv: 0, monthlyPayment: 0, overpayment: 0 }
    }
    return new LoanCalculator(+amount, +period, rate, repaymentMethod).getCalcResults()
  }, [amount, matchedProgram, period, rate, repaymentMethod])

  return (
    <Card twStyle={tw`mt-4 space-y-4`}>
      <div className='flex items-center justify-between'>
        <CalcResultLabel>{t('loan.monthly-payment', { ns: 'common' })}</CalcResultLabel>
        <CalcResultText twStyle={tw`text-xl leading-6`}>
          {calcResults.monthlyPayment <= 0 ? '0 ₸' : `${numberWithSpaces(calcResults?.monthlyPayment)} ₸`}
        </CalcResultText>
      </div>
      <div className='flex items-center justify-between'>
        <CalcResultLabel>{t('loan.interest-rate-full', { ns: 'common' })}</CalcResultLabel>
        <CalcResultText twStyle={tw`text-[#0F0F0F] dark:text-[#FFFFFF]`}>{rate} %</CalcResultText>
      </div>
      <div className='flex items-center justify-between'>
        <CalcResultLabel>{t('loan.gesv-percent', { ns: 'common' })}</CalcResultLabel>
        <CalcResultText twStyle={tw`text-[#0F0F0F] dark:text-[#FFFFFF]`}>
          {calcResults.gesv ? (!calcResults.gesv ? '-' : `${calcResults.gesv} %`) : '0'}
        </CalcResultText>
      </div>
      <div className='flex items-center justify-between'>
        <CalcResultLabel>{t('loan.overpayment', { ns: 'common' })}</CalcResultLabel>
        <CalcResultText twStyle={tw`text-[#0F0F0F] dark:text-[#FFFFFF]`}>
          {calcResults.overpayment <= 0 ? '0 ₸' : `${numberWithSpaces(calcResults?.monthlyPayment)} ₸`}
        </CalcResultText>
      </div>
    </Card>
  )
}

export default SuperappCalculator
